import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { 
  UserGroupIcon,
  AcademicCapIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  EyeIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  IdentificationIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { getExpiringCompetencies } from "../../../../api/hse/competency";
import { toast } from "react-hot-toast";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Expiring - Expiring competencies alerts
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyExpiring = () => {
  const { t } = useTranslation();
  
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    days: 30,
  });

  useEffect(() => {
    fetchExpiringCompetencies();
  }, [filters]);

  const fetchExpiringCompetencies = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        days: filters.days || 30,
      };
      
      const response = await getExpiringCompetencies(apiFilters);
      setCompetencies(response.competencies || []);
    } catch (err) {
      console.error("Error fetching expiring competencies:", err);
      setError(true);
      toast.error(t("Kunne ikke laste utløpende kompetanse"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      days: newFilters.days || 30,
    });
  };

  const getStatusBadge = (competency) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    const now = new Date();
    const expiresAt = competency.expires_at ? new Date(competency.expires_at) : null;
    
    if (!expiresAt) return null;
    
    const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return (
        <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          {t("Utløpt")}
        </span>
      );
    } else {
      return (
        <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
          <ClockIcon className="h-3 w-3 mr-1" />
          {t("Utløper om")} {daysUntilExpiry} {t("dager")}
        </span>
      );
    }
  };

  const competencyTypes = [
    { value: "cv", label: t("CV"), icon: DocumentTextIcon },
    { value: "certificate", label: t("Sertifikat"), icon: DocumentCheckIcon },
    { value: "course", label: t("Kurs"), icon: AcademicCapIcon },
    { value: "license", label: t("Lisens"), icon: IdentificationIcon },
  ];

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste utløpende kompetanse")]} />;
  }

  return (
    <HsePageChrome
      title={t("Utløper snart")}
      subtitle={t("Kompetanse og kurs som utløper snart")}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <CompetencySubnav />
          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i utløpende kompetanse...")}
          />

          {/* Expiring Competencies List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Utløpende kompetanse")} ({competencies.length})
              </h3>
              
              {competencies.length === 0 ? (
                <ResourceEmpty message={t("Ingen utløpende kompetanse funnet")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Ansatt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Kompetanse")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Utløper")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {competencies.map((competency) => (
                        <tr key={competency.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <UserGroupIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div>
                                <div className="font-medium text-gray-900">
                                  {competency.employee_name || competency.employee?.name || competency.employee?.email}
                                </div>
                                {competency.employee?.department_name && (
                                  <div className="text-gray-500 text-xs mt-1">
                                    {competency.employee.department_name}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              {competency.type === "cv" && <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-3" />}
                              {competency.type === "certificate" && <DocumentCheckIcon className="h-5 w-5 text-green-600 mr-3" />}
                              {competency.type === "course" && <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-3" />}
                              {competency.type === "license" && <IdentificationIcon className="h-5 w-5 text-orange-600 mr-3" />}
                              {(!competency.type || competency.type === "other") && <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-3" />}
                              <div>
                                <div className="font-medium text-gray-900">
                                  {competency.competency_definition?.name || competency.name}
                                </div>
                                {competency.type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                    {competencyTypes.find(t => t.value === competency.type)?.label || competency.type}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {competency.expires_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(competency.expires_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {getStatusBadge(competency)}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/dashboard/hse-ks/competency/employees/${competency.employee_id || competency.employee?.id}`}
                              className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              {t("Vis")}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

